/**
 * The atlas painter: a die's colour, relief and gloss atlases cut from the
 * numeral sheet, the card stock and a skin's tiles, one cell per face.
 *
 * Pure canvas 2D, so it runs the same on the main thread (`dieAssets`, for
 * the board, the tray and the roll-off) and inside a worker on an
 * `OffscreenCanvas` (`atlas.worker.js`, for the store shelf, where cutting
 * on the main thread would hitch the scroll). `env` is what differs: how a
 * canvas is made and where the images come from. What is painted does not.
 *
 * Constants are shared with `chamferedGeometry`, which lays the faces out
 * into exactly these cells.
 */
import { normalFromHeight } from './relief';
import {
  dieGeometry,
  vCross,
  vDot,
  vSub,
} from './geometry';

const CELL = 256;
/**
 * How much of a skin tile one face samples, as a fraction of the tile's short
 * side. A cell-sized 1:1 crop takes a sixteenth of a 1024px tile, which lands
 * inside a single swirl lobe and reads as flat colour; a wider window keeps
 * the swirl legible on the face while the random offset still drifts the
 * colour from face to face. Paper stock is unaffected: its grain is authored
 * at cell scale and must stay 1:1.
 */
const SKIN_WINDOW = 0.55;
/** Slope-to-tilt gain when the height atlas becomes a normal map. */
const NORMAL_STRENGTH = 2.2;
/** Mid grey: the flat height every atlas starts from. */
const HEIGHT_FLAT = '#8a8a8a';
/**
 * Roughness the pencilled numerals leave on a skin. Graphite is a touch
 * glossier than paper; in the map it sits as a flat value under the numeral.
 */
const NUMERAL_ROUGHNESS = 0.62;
/** Ring radius of the extra numeral passes at full weight, as a fraction of the numeral's height. */
const NUMERAL_WEIGHT_SPREAD = 0.05;
/** How far a face is pulled in toward the chamfer. */
const CHAMFER = 0.105;

/** Layout of the numeral sheet baked by Armarium's `pencil-die-faces` tool. */
const SHEET_CELL = 160;
const SHEET_COLS = 6;
/**
 * How much of its cell the drawn numeral occupies. From this follows how large
 * the cell must land on the face for the numeral to get the requested height.
 */
const SHEET_FILL = 0.6;
/** The card tone, as long as the paper image has not arrived yet. */
const CARD = '#a98f66';

const NUMERAL_SIZE = { 4: 0.19, 6: 0.42, 8: 0.36, 10: 0.32, 12: 0.34, 20: 0.36 };
/**
 * Where the d4's corner numerals sit: this far from the face center toward
 * the corner, as a share of the corner distance.
 */
const D4_CORNER_REACH = 0.56;
/**
 * How much of the atlas cell the face's farthest vertex reaches. The border
 * stays ground color, so nothing bleeds into the neighbouring cell.
 */
const FACE_FILL = 0.41;

/** Layout of the atlas cells, per side count: shared with the geometry's UVs. */
export function atlasLayout(sides) {
  const cols = Math.ceil(Math.sqrt(sides + 1));
  return { cols, rows: Math.ceil((sides + 1) / cols) };
}

/** Center of atlas cell `index` in UV coordinates (V points up). */
export function cellCenter(sides, index) {
  const { cols, rows } = atlasLayout(sides);
  const col = index % cols;
  const row = Math.floor(index / cols);
  return [(col + 0.5) / cols, 1 - (row + 0.5) / rows];
}

export function faceFrame(geometry, face) {
  const center = geometry.centers[face];
  const up = geometry.ups[face];
  const right = vCross(up, geometry.normals[face]);
  const reach = Math.max(
    ...geometry.faces[face].map((vi) => {
      const d = vSub(geometry.vertices[vi], center);
      return Math.hypot(vDot(d, right), vDot(d, up));
    }),
  );
  return { center, right, up, reach };
}

export { CARD, CHAMFER, FACE_FILL };

/** Whether a palette carries its own roughness map, in which case the map is the roughness. */
export function hasRoughnessMap(palette) {
  return Boolean(palette.roughness || palette.roughnessImage);
}

/**
 * A painter bound to an environment:
 *   createCanvas()          a fresh 2D-capable canvas (HTMLCanvasElement or OffscreenCanvas)
 *   numeralSheet()          the numeral sheet image, or null while it is on its way
 *   cardStock()             the card stock image, or null
 *   skinImage(palette, key) one of a skin's images (`tile`, `height`, `roughness`), or null
 *
 * `paintAtlases(sides, scale, palette)` returns the three atlases as
 * canvases: `map` (colour), `normal` (tangent-space normals from the height
 * atlas) and `roughness`.
 */
export function createAtlasPainter(env) {
  function seededRandom(seed) {
    let s = seed >>> 0;
    return () => {
      s = (s * 1664525 + 1013904223) >>> 0;
      return s / 4294967296;
    };
  }

  /**
   * Paint a body's numerals into a drawing atlas. `paint` gets the cell center in
   * pixels per face; the last cell stays blank ground for chamfers and corners.
   */
  function drawAtlas(sides, paint, scale = 1) {
    const { cols, rows } = atlasLayout(sides);
    const canvas = env.createCanvas();
    canvas.width = cols * CELL * scale;
    canvas.height = rows * CELL * scale;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return canvas;
    // Every painter works in cell units; the scale only buys texels.
    ctx.scale(scale, scale);
    const { values } = dieGeometry(sides);
    for (let i = 0; i <= sides; i++) {
      const x = (i % cols) * CELL + CELL / 2;
      const y = Math.floor(i / cols) * CELL + CELL / 2;
      ctx.save();
      paint(ctx, { x, y, face: i < sides ? i : null, value: i < sides ? values[i] : null });
      ctx.restore();
    }
    return canvas;
  }


  function numeralCell(value) {
    if (value === 6) return 20;
    if (value === 9) return 21;
    return value - 1;
  }

  /**
   * The ground of a face: a cutout of the card stock, taken from a different
   * spot per cell. The same cutout on twenty faces would look stamped. And
   * exactly so once the die turns. `window` widens the sampled square (as a
   * fraction of the source's short side) and scales it down into the cell, so a
   * skin tile shows its swirl rather than one lobe of it.
   */
  function paintCard(ctx, x, y, seed, fill = CARD, source = env.cardStock(), window = 0) {
    ctx.fillStyle = fill;
    ctx.fillRect(x - CELL / 2, y - CELL / 2, CELL, CELL);
    if (!source) return;
    const span = window > 0 ? Math.min(source.width, source.height) * window : CELL;
    const rand = seededRandom(seed);
    const sx = rand() * Math.max(1, source.width - span);
    const sy = rand() * Math.max(1, source.height - span);
    ctx.drawImage(source, sx, sy, span, span, x - CELL / 2, y - CELL / 2, CELL, CELL);
  }



  /**
   * A face's ground in the current palette. Paper palettes cut the card stock
   * through their filter; a skin cuts its baked tile. The blank cell (chamfers
   * and corners) of a skin leans on the fill colour, so the die's rim reads as
   * one tone with only a hint of the swirl.
   */
  function paintGround(ctx, x, y, seed, palette, isEdgeCell) {
    const skinned = Boolean(palette.tile || palette.tileImage);
    ctx.save();
    if (skinned) {
      ctx.fillStyle = palette.fill;
      ctx.fillRect(x - CELL / 2, y - CELL / 2, CELL, CELL);
      if (isEdgeCell) ctx.globalAlpha = 0.5;
      paintCard(ctx, x, y, seed, palette.fill, env.skinImage(palette, 'tile'), SKIN_WINDOW);
    } else {
      ctx.filter = palette.cardFilter;
      paintCard(ctx, x, y, seed, palette.fill);
    }
    ctx.restore();
  }

  /**
   * **The worn edge.** A die that spent years in a bag is darker at the edges
   * than across the face. Without this gradient every face is evenly bright and
   * the body looks freshly pressed. Clean, and therefore wrong.
   */
  function paintWear(ctx, x, y, isEdgeCell, wear) {
    const g = ctx.createRadialGradient(x, y, CELL * 0.2, x, y, CELL * 0.62);
    g.addColorStop(0, `rgb(${wear} / 0)`);
    g.addColorStop(0.6, `rgb(${wear} / ${isEdgeCell ? 0.22 : 0.1})`);
    g.addColorStop(1, `rgb(${wear} / ${isEdgeCell ? 0.62 : 0.4})`);
    ctx.fillStyle = g;
    ctx.fillRect(x - CELL / 2, y - CELL / 2, CELL, CELL);
  }

  let tintScratch = null;

  /**
   * The numeral's cell from the sheet, dyed a flat colour. The graphite's alpha
   * is the shape; `source-in` keeps it and swaps the grey for the dye.
   */
  function tintedNumeral(i, color) {
    tintScratch ??= env.createCanvas();
    tintScratch.width = SHEET_CELL;
    tintScratch.height = SHEET_CELL;
    const ctx = tintScratch.getContext('2d');
    if (ctx === null) return null;
    ctx.drawImage(
      env.numeralSheet(),
      (i % SHEET_COLS) * SHEET_CELL,
      Math.floor(i / SHEET_COLS) * SHEET_CELL,
      SHEET_CELL,
      SHEET_CELL,
      0,
      0,
      SHEET_CELL,
      SHEET_CELL,
    );
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, SHEET_CELL, SHEET_CELL);
    return tintScratch;
  }

  /**
   * The drawn numeral from the sheet, `height` pixels tall, centered on (x, y).
   * `ink` is either a CSS filter over the graphite or a colour it is dyed.
   */
  function paintNumeral(ctx, x, y, value, height, angle = 0, ink = { filter: 'none' }) {
    const numeralSheet = env.numeralSheet();
    if (numeralSheet === null) return;
    const i = numeralCell(value);
    const size = height / SHEET_FILL;
    const dyed = ink.color ? tintedNumeral(i, ink.color) : null;
    ctx.save();
    ctx.filter = ink.filter ?? 'none';
    ctx.translate(x, y);
    ctx.rotate(angle);
    // The pencil goes over the line again: `numeralWeight` adds passes on a
    // small ring around the glyph, which closes the hatching and thickens the
    // stroke without losing the hand-drawn edge.
    const weight = ink.weight ?? 0;
    const passes = weight > 0 ? 1 + Math.round(weight * 6) : 1;
    const spread = weight * height * NUMERAL_WEIGHT_SPREAD;
    for (let k = 0; k < passes; k++) {
      const dx = k === 0 ? 0 : Math.cos((k / (passes - 1)) * Math.PI * 2) * spread;
      const dy = k === 0 ? 0 : Math.sin((k / (passes - 1)) * Math.PI * 2) * spread;
      if (dyed) {
        ctx.drawImage(dyed, dx - size / 2, dy - size / 2, size, size);
      } else {
        ctx.drawImage(
          numeralSheet,
          (i % SHEET_COLS) * SHEET_CELL,
          Math.floor(i / SHEET_COLS) * SHEET_CELL,
          SHEET_CELL,
          SHEET_CELL,
          dx - size / 2,
          dy - size / 2,
          size,
          size,
        );
      }
    }
    ctx.restore();
  }

  /**
   * The numerals of one face. One in the middle, or for the d4 one in each
   * corner: the vertex's own value, its head pointing at that corner so it reads
   * upright when the corner is the one at the ceiling.
   */
  function paintFaceNumerals(ctx, x, y, sides, face, ink = { filter: 'none' }) {
    const geometry = dieGeometry(sides);
    const height = CELL * NUMERAL_SIZE[sides];
    if (!geometry.vertexUp) {
      paintNumeral(ctx, x, y, geometry.values[face], height, 0, ink);
      return;
    }
    const frame = faceFrame(geometry, face);
    for (const vi of geometry.faces[face]) {
      const d = vSub(geometry.vertices[vi], frame.center);
      // Face space to cell pixels: `right` runs along +x, `up` along -y.
      const px = vDot(d, frame.right) / frame.reach;
      const py = -vDot(d, frame.up) / frame.reach;
      const at = FACE_FILL * CELL * D4_CORNER_REACH;
      paintNumeral(
        ctx,
        x + px * at,
        y + py * at,
        geometry.values[vi],
        height,
        Math.atan2(px, -py),
        ink,
      );
    }
  }

  /**
   * Relief and gloss of one atlas cell as flat images, from which the normal
   * and roughness maps are cut. A skin brings its own height and roughness
   * tiles from the lab, cut at the same seed and window as its colour so the
   * three line up on every face; paper falls back to the stock's tooth and no
   * roughness variation at all.
   */
  function paintHeight(ctx, x, y, seed, palette) {
    const height = env.skinImage(palette, 'height');
    ctx.save();
    if (height) {
      paintCard(ctx, x, y, seed, HEIGHT_FLAT, height, SKIN_WINDOW);
    } else {
      ctx.fillStyle = HEIGHT_FLAT;
      ctx.fillRect(x - CELL / 2, y - CELL / 2, CELL, CELL);
      ctx.globalAlpha = 0.8;
      ctx.filter = 'grayscale(1) contrast(2.1)';
      paintCard(ctx, x, y, seed);
    }
    ctx.restore();
  }

  function paintRoughness(ctx, x, y, seed, palette) {
    const roughness = env.skinImage(palette, 'roughness');
    ctx.save();
    if (roughness) {
      paintCard(ctx, x, y, seed, '#ffffff', roughness, SKIN_WINDOW);
    } else {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(x - CELL / 2, y - CELL / 2, CELL, CELL);
    }
    ctx.restore();
  }

  /** An atlas canvas turned into a tangent-space normal map of the same size. */
  function normalAtlas(heightCanvas, scale = 1) {
    const ctx = heightCanvas.getContext('2d');
    if (ctx === null) return heightCanvas;
    const { width, height } = heightCanvas;
    // Twice the texels halves the slope per texel; the gain keeps the relief where it was.
    const normal = normalFromHeight(ctx.getImageData(0, 0, width, height), NORMAL_STRENGTH * scale);
    const out = env.createCanvas();
    out.width = width;
    out.height = height;
    out.getContext('2d')?.putImageData(new ImageData(normal.data, width, height), 0, 0);
    return out;
  }

  /**
   * The atlas: one cell per face with card and numeral, the last cell blank card
   * for the chamfers and corners.
   *
   * Plus a relief cut from the **same images**: the tooth of the paper (or the
   * skin's height tile) is the grain, and where graphite lies it lies *in* the
   * tooth, a touch deeper. The relief ships as a normal map; three.js reads a
   * bump map only in the absence of one, and the skins need the normal.
   *
   * And a roughness atlas: white (the material's own roughness, untouched) for
   * paper, the skin's roughness tile for a skin, with the numerals laid in at
   * graphite's gloss either way.
   */
  function paintAtlases(sides, scale, palette) {
    const seed = (value) => sides * 31 + (value ?? 0) * 7 + 5;

    const map = drawAtlas(sides, (ctx, { x, y, face, value }) => {
      paintGround(ctx, x, y, seed(value), palette, face === null);
      paintWear(ctx, x, y, face === null, palette.wear);
      if (face === null) return;
      const ink = palette.numeralColor ? { color: palette.numeralColor } : { filter: palette.numeralFilter };
      ink.weight = palette.numeralWeight;
      paintFaceNumerals(ctx, x, y, sides, face, ink);
    }, scale);

    const height = drawAtlas(sides, (ctx, { x, y, face, value }) => {
      paintHeight(ctx, x, y, seed(value), palette);
      if (face === null) return;
      ctx.save();
      ctx.globalAlpha = 0.7;
      paintFaceNumerals(ctx, x, y, sides, face);
      ctx.restore();
    }, scale);

    const roughness = drawAtlas(sides, (ctx, { x, y, face, value }) => {
      paintRoughness(ctx, x, y, seed(value), palette);
      if (face === null || !hasRoughnessMap(palette)) return;
      const grey = Math.round(NUMERAL_ROUGHNESS * 255);
      paintFaceNumerals(ctx, x, y, sides, face, { color: `rgb(${grey} ${grey} ${grey})` });
    }, scale);

    return { map, normal: normalAtlas(height, scale), roughness };
  }

  return { paintAtlases };
}
